import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HelpCircle, Search, ChevronDown, ChevronUp } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

function useLangPreference() {
  const initial = (typeof document !== "undefined" && document.documentElement.getAttribute("dir") === "rtl") ? "ar" : "en";
  const [lang, setLang] = useState(() => localStorage.getItem("faq_lang") || initial);
  useEffect(() => { try { localStorage.setItem("faq_lang", lang); } catch {} }, [lang]);
  return [lang, setLang];
}

function FaqCard({ entry, lang, expanded, onToggle }) {
  const q = lang === "ar" ? (entry.question_ar || entry.question_en) : (entry.question_en || entry.question_ar);
  const a = lang === "ar" ? (entry.answer_ar || entry.answer_en) : (entry.answer_en || entry.answer_ar);
  return (
    <div className="hah-card" data-testid={`faq-card-${entry.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-[#F5FBFC] rounded-lg"
        data-testid={`faq-toggle-${entry.id}`}
      >
        <div className="min-w-0" dir={lang === "ar" ? "rtl" : "ltr"}>
          <div className="text-[15px] font-semibold text-[#1F2937]" data-testid={`faq-question-${entry.id}`}>{q}</div>
          {entry.category && (
            <div className="mt-1 text-[10px] uppercase tracking-wider font-semibold text-[#0B7E8C] inline-block bg-[#E6F5F7] rounded px-1.5 py-[1px]">
              {entry.category}
            </div>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#6B7280] shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#6B7280] shrink-0" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-0 text-sm text-[#374151] whitespace-pre-wrap leading-relaxed"
             dir={lang === "ar" ? "rtl" : "ltr"}
             data-testid={`faq-answer-${entry.id}`}>
          {a}
        </div>
      )}
    </div>
  );
}

export default function FaqPage() {
  const [items, setItems] = useState(null);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("");
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [lang, setLang] = useLangPreference();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/faq");
      setItems(r.data.items || []);
      setCategories(r.data.categories || []);
    } catch (e) { toast.error(formatApiError(e)); setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!items) return [];
    let list = items;
    if (cat) list = list.filter((i) => i.category === cat);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((i) => (
        (i.question_en || "").toLowerCase().includes(q) ||
        (i.question_ar || "").includes(query.trim()) ||
        (i.answer_en || "").toLowerCase().includes(q) ||
        (i.answer_ar || "").includes(query.trim())
      ));
    }
    return list;
  }, [items, cat, query]);

  const grouped = useMemo(() => {
    const g = new Map();
    for (const i of filtered) {
      const k = (i.category || "").trim() || "General";
      if (!g.has(k)) g.set(k, []);
      g.get(k).push(i);
    }
    return Array.from(g.entries());
  }, [filtered]);

  return (
    <div data-testid="faq-page">
      <PageHeader
        title="Frequently Asked Questions"
        subtitle="Quick answers to common questions about the platform, workflows, and clinical operations."
        icon={HelpCircle}
      />

      <div className="flex flex-col md:flex-row gap-2 md:items-center mb-4">
        <div className="relative flex-1 max-w-xl">
          <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search questions and answers…"
            className="pl-9"
            data-testid="faq-search-input"
          />
        </div>
        <Select value={cat || "all"} onValueChange={(v) => setCat(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full md:w-[200px]" data-testid="faq-category-filter">
            <SelectValue>{cat || "All categories"}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="inline-flex items-center rounded-md border border-[#E5E7EB] bg-white p-0.5"
             data-testid="faq-lang-toggle">
          <button type="button" onClick={() => setLang("en")}
            className={`px-2.5 py-1 text-xs rounded ${lang === "en" ? "bg-[#0B7E8C] text-white" : "text-[#6B7280]"}`}
            data-testid="faq-lang-en">EN</button>
          <button type="button" onClick={() => setLang("ar")}
            className={`px-2.5 py-1 text-xs rounded ${lang === "ar" ? "bg-[#0B7E8C] text-white" : "text-[#6B7280]"}`}
            data-testid="faq-lang-ar">AR</button>
        </div>
      </div>

      {items === null ? (
        <div className="text-sm text-[#6B7280]" data-testid="faq-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="hah-card p-8 text-center text-sm text-[#6B7280]" data-testid="faq-empty">
          {items.length === 0
            ? "No FAQs published yet. Admins can add entries under Admin Console → FAQ Management."
            : "No FAQs match your search."}
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([groupName, entries]) => (
            <div key={groupName} data-testid={`faq-group-${groupName}`}>
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-2">{groupName}</div>
              <div className="space-y-2">
                {entries.map((entry) => (
                  <FaqCard
                    key={entry.id}
                    entry={entry}
                    lang={lang}
                    expanded={expandedId === entry.id}
                    onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
